<template>
    <div class="DebugMenuSection">
        <div class="mt-2 flex items-center justify-between border-t border-theme-border px-2 pt-2">
            <span class="text-[10px] font-semibold uppercase tracking-wider text-theme-toolbar-icon">Dev</span>
            <button
                :class="copyButtonClass"
                title="Copy a full-config URL that reproduces this exact reader setup"
                @click="copyConfigUrl"
            >
                {{ copied ? 'Copied!' : 'Copy test URL' }}
            </button>
        </div>

        <!-- Pill groups: file type, content sets, layout, theme -->
        <div v-for="group in pillGroups" :key="group.key" class="px-2 py-1.5">
            <div class="mb-1 flex items-center gap-1.5 text-[11px] text-theme-toolbar-icon">
                <span>{{ group.label }}</span>
                <span
                    v-if="group.reloads"
                    class="rounded-sm bg-theme-icon-bg px-1 text-[9px] uppercase tracking-wide text-theme-toolbar-icon"
                    title="Changing this reloads the page"
                    >reloads</span
                >
            </div>
            <div class="flex flex-wrap gap-1">
                <button
                    v-for="option in group.options"
                    :key="option.value"
                    :class="pillClass(group.current === option.value)"
                    @click="selectOption(group, option.value)"
                >
                    {{ option.label }}
                </button>
            </div>
        </div>

        <!-- On/off mode simulations -->
        <div class="px-2 py-1.5">
            <div class="mb-1 flex items-center gap-1.5 text-[11px] text-theme-toolbar-icon">
                <span>Modes</span>
                <span
                    class="rounded-sm bg-theme-icon-bg px-1 text-[9px] uppercase tracking-wide text-theme-toolbar-icon"
                    title="Changing these reloads the page"
                    >reloads</span
                >
            </div>
            <div class="flex flex-wrap gap-1">
                <button
                    v-for="toggle in modeToggles"
                    :key="toggle.key"
                    :class="pillClass(toggle.active)"
                    @click="toggleMode(toggle)"
                >
                    {{ toggle.label }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import {
    DEV_OPTION_SPECS,
    devMenuScope,
    getDevOptionSpec,
    readDevConfig,
} from '../../../src/js/TestState/devConfig.js';

// Human-readable labels for option values that aren't already nicely cased.
const VALUE_LABELS = {
    pdf: 'PDF',
    epub: 'EPUB',
    audio: 'Audio',
    'stress-532': 'Stress 532',
    'fixed-layout': 'Fixed layout',
    'high-contrast-light': 'HC light',
    'high-contrast-dark': 'HC dark',
};

const labelFor = value => VALUE_LABELS[value] || value.charAt(0).toUpperCase() + value.slice(1);

const toOptions = spec => spec.allowed.map(value => ({ value, label: labelFor(value) }));

export default {
    data() {
        // The resolved dev config (URL > localStorage > default) is the source
        // of truth for which pills render active on load.
        return { devConfig: readDevConfig(), copied: false, copyTimer: null };
    },
    computed: {
        ...mapState(['reader', 'ui', 'fileType', 'previewMode', 'customPreview', 'embeddedMode']),
        ...mapGetters(['scrollPageMode', 'doublePageMode']),
        // 'full' in local/E2E (every knob), 'staging' in staging (only knobs
        // flagged `staging: true`). Drives which options the menu renders.
        menuScope() {
            return devMenuScope();
        },
        // The layout the reader is actually in, in the spec's vocabulary. Derived from the
        // same getters the rest of the UI reads rather than from settings.scroll /
        // syntheticSpread directly, so the pill cannot drift from what the reader does —
        // a pill with its own idea of the layout is the bug this replaces.
        liveEpubLayout() {
            if (this.scrollPageMode) return 'scroll';
            return this.doublePageMode ? 'double' : 'single';
        },
        activeFileType() {
            return this.fileType || this.devConfig.fileType;
        },
        // Pill groups: { key, label, current, reloads, options, formats }.
        pillGroups() {
            const groups = [
                this.buildGroup('fileType', 'File type'),
                this.buildGroup('pdfSet', 'PDF set'),
                this.buildGroup('testArticles', 'Article set'),
                this.buildGroup('epubSet', 'EPUB set'),
                this.buildGroup('pdfLayout', 'PDF layout', this.reader.layout),
                this.buildGroup('epubLayout', 'EPUB layout', this.liveEpubLayout),
                // ui.theme holds the raw selection (incl. 'system'). themeSelected
                // is the resolved light/dark scheme, so it would never match 'system'.
                this.buildGroup('theme', 'Theme', this.ui.theme),
            ];
            return groups.filter(group => this.specVisible(group.spec) && this.appliesToFormat(group.formats));
        },
        modeToggles() {
            const liveState = {
                preview: this.previewMode,
                customPreview: this.customPreview,
                embedded: this.embeddedMode,
            };
            return [
                { key: 'preview', label: 'Preview' },
                { key: 'anonymous', label: 'Anonymous' },
                { key: 'concurrency', label: 'Concurrency' },
                { key: 'concurrencyDebug', label: 'Concurrency 10s' },
                { key: 'embedded', label: 'Embedded' },
                { key: 'customPreview', label: 'Custom preview' },
                { key: 'epubPagebreaks', label: 'Pagebreaks' },
                { key: 'addOnsEditing', label: 'Add-ons editing' },
                { key: 'addOnsPanel', label: 'Add-ons panel' },
                { key: 'richText', label: 'Rich text' },
            ]
                .filter(toggle => {
                    const spec = getDevOptionSpec(toggle.key);
                    return this.specVisible(spec) && this.appliesToFormat(spec.formats);
                })
                .map(toggle => ({
                    ...toggle,
                    param: getDevOptionSpec(toggle.key).param,
                    // Reflect live store state where available, else the resolved config.
                    active: toggle.key in liveState ? liveState[toggle.key] : this.devConfig[toggle.key],
                }));
        },
        // Styling for the "Copy test URL" button. Flips to the accent colours
        // briefly after the copy action.
        copyButtonClass() {
            return [
                'rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent',
                this.copied
                    ? 'bg-theme-buynow-bg text-theme-buynow-text'
                    : 'bg-theme-icon-bg text-theme-text hover:opacity-80',
            ];
        },
    },
    beforeDestroy() {
        if (this.copyTimer) {
            clearTimeout(this.copyTimer);
        }
    },
    methods: {
        ...mapMutations(['setReaderLayout']),
        /**
         * Build a pill group from a spec. `current` overrides the active value
         * with live store state for live knobs (so the pill updates instantly).
         */
        buildGroup(key, label, current = null) {
            const spec = getDevOptionSpec(key);
            return {
                key,
                label,
                spec,
                formats: spec.formats,
                reloads: spec.mode === 'reload',
                current: current !== null ? current : this.devConfig[key],
                options: toOptions(spec),
            };
        },
        appliesToFormat(formats) {
            return formats === 'all' || formats.includes(this.activeFileType);
        },
        // In staging only the `staging`-flagged knobs render; local/E2E shows all.
        specVisible(spec) {
            return this.menuScope === 'full' || spec.staging === true;
        },
        pillClass(isActive) {
            return [
                'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2',
                isActive
                    ? 'bg-theme-buynow-bg text-theme-buynow-text'
                    : 'bg-theme-icon-bg text-theme-text hover:opacity-80',
            ];
        },
        selectOption(group, value) {
            if (group.spec.mode === 'live') {
                this.applyLive(group.key, value);
                this.writeParam(group.spec.param, value);
                return;
            }
            // Reload knobs swap loaded content/flags, so they reload.
            this.updateUrlAndReload(this.reloadParamsFor(group.key, value));
        },
        // Apply a live knob via its store mutation so it changes without reload.
        applyLive(key, value) {
            if (key === 'pdfLayout') {
                this.setReaderLayout(value);
            } else if (key === 'epubLayout') {
                // EpubViewer listens on the `layout` bus event and re-paginates
                // live via Readium — the same path the settings UI uses.
                this.$bus.$emit('layout', value);
            } else if (key === 'theme') {
                this.$store.dispatch('setTheme', { theme: value });
            }
        },
        reloadParamsFor(key, value) {
            const spec = getDevOptionSpec(key);
            // Switching file type clears type-specific params so a stale set
            // doesn't leak into the new type's session.
            if (key === 'fileType') {
                return { file_type: value, epub_set: null, test_articles: null, pdf_set: null };
            }
            // Explicit: write the param even when it equals the default so the
            // URL fully documents the config and stays shareable.
            return { [spec.param]: value };
        },
        toggleMode(toggle) {
            // Explicit on/off so the URL documents the state either way.
            const next = toggle.active ? '0' : '1';
            const params = { [toggle.param]: next };
            // Concurrency 10s implies Concurrency (see devConfig), so turning
            // Concurrency off must also force the debug param off or it
            // re-derives concurrency on reload.
            if (toggle.key === 'concurrency' && next === '0') {
                params[getDevOptionSpec('concurrencyDebug').param] = '0';
            }
            this.updateUrlAndReload(params);
        },
        // Write a param into the URL without reloading (URL is source of truth).
        // Explicit: even default values are written so the address bar always
        // documents the live config and is directly shareable.
        writeParam(param, value) {
            const url = new URL(window.location.href);
            url.searchParams.set(param, value);
            window.history.replaceState(window.history.state, '', url.toString());
            this.devConfig = readDevConfig();
        },
        updateUrlAndReload(params) {
            const url = new URLSearchParams(window.location.search);
            for (const [key, value] of Object.entries(params)) {
                if (value === null) {
                    url.delete(key);
                } else {
                    url.set(key, value);
                }
            }
            window.location.search = url.toString();
        },
        // Current value of one option for serialization: prefer live store
        // state, fall back to the resolved dev config.
        liveValueFor(spec) {
            switch (spec.key) {
                case 'fileType':
                    return this.activeFileType;
                case 'pdfLayout':
                    return this.reader.layout;
                case 'theme':
                    return this.ui.theme;
                case 'preview':
                    return this.previewMode;
                case 'customPreview':
                    return this.customPreview;
                case 'embedded':
                    return this.embeddedMode;
                default:
                    return this.devConfig[spec.key];
            }
        },
        // Build an absolute, fully-explicit config URL: every dev param pinned
        // to its current value so the link reproduces this exact setup
        // regardless of future default changes.
        buildConfigUrl() {
            // Only serialize knobs visible in this environment: in staging the
            // hidden content/session params are inert (the host owns them), so
            // pinning them would only clutter the shareable URL. Strip every dev
            // param first — including hidden ones already in the URL — so they are
            // never carried over, then re-add only the visible ones.
            const specs = DEV_OPTION_SPECS.filter(spec => this.specVisible(spec));
            const url = new URL(window.location.href);
            for (const spec of DEV_OPTION_SPECS) {
                url.searchParams.delete(spec.param);
            }
            for (const spec of specs) {
                const value = this.liveValueFor(spec);
                url.searchParams.set(spec.param, spec.type === 'boolean' ? (value ? '1' : '0') : value);
            }
            return url.toString();
        },
        // Copy a shareable full-config URL to the clipboard (prompt fallback
        // when the Clipboard API is unavailable, e.g. an insecure context).
        async copyConfigUrl() {
            const url = this.buildConfigUrl();
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(url);
                } else {
                    window.prompt('Copy this Volpe test URL:', url);
                }
            } catch {
                window.prompt('Copy this Volpe test URL:', url);
            }
            this.flashCopied();
        },
        flashCopied() {
            this.copied = true;
            if (this.copyTimer) {
                clearTimeout(this.copyTimer);
            }
            this.copyTimer = setTimeout(() => {
                this.copied = false;
            }, 1500);
        },
    },
};
</script>
